import React, { useEffect } from 'react';

import Auth from '../../components/Auth/Auth';
import AuthConfirmPage from '../../components/AuthConfirmPage';
import DashboardSelector from '../../components/DashboardSelector';
import HomePage from '../../components/HomePage/HomePage';
import ForgotPasswordPage from '../../components/LoginPage/ForgotPasswordPage';
import ResetPasswordPage from '../../components/ResetPasswordPage';
import { useAuthentication } from '../../hooks/useAuthentication';
import { PlanStatusProvider } from '../../hooks/usePlanStatus';
import { supabase } from '../../lib/supabase';
import { POST_AUTH_LANDING_PATH } from './postAuthLanding';
import { isProtectedRoute } from './routeRegistry';
import { clearPaymentFlowFlags, isPaymentFlowInProgress, isPaymentReturnPath } from '../../utils/paymentReturnBootstrap';
// SingleOptimizeFlow import removed — the /optimize route now redirects to
// /agent. The component lives in src/_deadcode/SingleOptimizeFlow/ for
// historical reference. Layout, GuestHeader, and GuestFooter were only used
// by that route and have also been removed from this file.
import DashboardContainer from '../../modules/dashboard/components/DashboardContainer';
import AboutUsPage from '../../pages/AboutUsPage';
import BlogPage from '../../pages/BlogPage';
import BlogPostPage from '../../pages/BlogPostPage';
import CareerPostPage from '../../pages/CareerPostPage';
import JobApplicationPage from '../../pages/JobApplicationPage';
import CareersPage from '../../pages/CareersPage';
import BusinessPage from '../../pages/BusinessPage';
import PublicJobBoardPage from '../../pages/PublicJobBoardPage';
import SuccessStoriesPage from '../../pages/SuccessStoriesPage';
import FeatureSmartMatchingPage from '../../pages/FeatureSmartMatchingPage';
import FeatureResumeOptimizerPage from '../../pages/FeatureResumeOptimizerPage';
import FeatureInterviewPrepPage from '../../pages/FeatureInterviewPrepPage';
import FeaturesOverviewPage from '../../pages/FeaturesOverviewPage';
import ProblemPage from '../../pages/ProblemPage';
import WhoItsForPage from '../../pages/WhoItsForPage';
import ResumeCheckPage from '../../pages/ResumeCheckPage';
import ContactUsPage from '../../pages/ContactUsPage';
import DevelopersPage from '../../pages/DevelopersPage';
import DebugLogs from '../../pages/DebugLogs';
import EmailUnsubscribePage from '../../pages/EmailUnsubscribePage';
import FAQPage from '../../pages/FAQPage';
import JoinCohortPage from '../../pages/JoinCohortPage';
import CookiePolicyPage from '../../pages/legal/CookiePolicyPage';
import DPDPCompliancePage from '../../pages/legal/DPDPCompliancePage';
import GDPRCompliancePage from '../../pages/legal/GDPRCompliancePage';
import PrivacyPolicyPage from '../../pages/legal/PrivacyPolicyPage';
import RefundCancellationPage from '../../pages/legal/RefundCancellationPage';
import TermsOfServicePage from '../../pages/legal/TermsOfServicePage';
import TrustCenterPage from '../../pages/legal/TrustCenterPage';
import AgentPage from '../../pages/AgentPage';
import NotFoundPage from '../../pages/NotFoundPage';
import ReferralPage from '../../pages/ReferralPage';
import SuccessStoryPage from '../../pages/SuccessStoryPage';
import AdminAnalytics from '../../modules/dashboard/components/AdminAnalytics';
import ControlPanel from '../../modules/dashboard/components/ControlPanel';
import TestimonialsAdminPage from '../../pages/admin/TestimonialsAdminPage';
import CronJobsAdminPage from '../../pages/admin/CronJobsAdminPage';
import TranslationsAdminPage from '../../pages/admin/TranslationsAdminPage';
const DevDataTablePage = import.meta.env.DEV
  ? React.lazy(() => import('../../pages/DevDataTablePage'))
  : null;
const DevCoachMarkPage = import.meta.env.DEV
  ? React.lazy(() => import('../../pages/DevCoachMarkPage'))
  : null;
import OnboardingPage from '../../pages/OnboardingPage';
import CreditsCheckoutPage from '../../pages/CreditsCheckoutPage';
import PaymentCheckoutPage from '../../pages/PaymentCheckoutPage';
import PaymentSuccessPage from '../../pages/PaymentSuccessPage';
import PaymentFailedPage from '../../pages/PaymentFailedPage';
import PricingPage from '../../pages/PricingPage';
import SignupConsentPage from '../../pages/SignupConsentPage';

export type AppView = 
  | 'home' 
  | 'login' 
  | 'signup'
  | 'signup-consent'
  | 'optimize'
  | 'onboarding'
  | 'dashboard'
  | 'agent'
  | 'referrals'
  | 'admin-analytics'
  | 'control-panel'
  | 'cron-jobs-admin'
  | 'testimonials-admin'
  | 'translations-admin'
  | 'dev-datatable'
  | 'dev-coachmark'
  | 'profile'
  | 'account-settings'
  | 'dashboard-selector'
  | 'forgot-password' 
  | 'reset-password' 
  | 'auth-confirm'
  | 'privacy-policy' 
  | 'cookie-policy' 
  | 'terms-of-service' 
  | 'gdpr-compliance'
  | 'dpdp-compliance'
  | 'trust-center'
  | 'refund-cancellation' 
  | 'faq'
  | 'faqs' 
  | 'about-us' 
  | 'blog'
  | 'blog-post'
  | 'careers'
  | 'careers-post'
  | 'careers-apply'
  | 'contact-us'
  | 'email-unsubscribe'
  | 'pricing'
  | 'credits-checkout'
  | 'payment-checkout'
  | 'payment-success' 
  | 'payment-history'
  | 'debug-logs'
  | 'join-cohort'
  | 'developers'
  | 'business'
  | 'job-board'
  | 'success-stories'
  | 'features-overview'
  | 'problem'
  | 'who-its-for'
  | 'feature-smart-matching'
  | 'feature-resume-optimizer'
  | 'feature-interview-prep'
  | 'resume-check'
  | 'success-story'
  | 'not-found';

interface AppRouterProps {
  currentView: AppView;
  onNavigate: (view: AppView) => void;
  onAuthSuccess: () => void;
}

// Helper function to cast AppView to string for components that expect string
const navigateHelper = (onNavigate: (view: AppView) => void) => 
  (view: string) => onNavigate(view as AppView);

const AppRouter: React.FC<AppRouterProps> = ({
  currentView,
  onNavigate,
  onAuthSuccess
}) => {
  // Get the actual sign-out function, session, and auth loading from authentication hook
  const { signOut, session, loading: authLoading } = useAuthentication();

  // Defense-in-depth: redirect signed-out users away from protected URLs.
  // Double-check with Supabase storage to avoid redirecting during transient
  // state gaps (e.g. token rotation, tab rehydration, post-OAuth).
  useEffect(() => {
    if (typeof window === 'undefined' || authLoading || session) return;
    const path = window.location.pathname;
    const search = window.location.search;
    if (isPaymentReturnPath(path, search)) return;
    if (isPaymentFlowInProgress()) return;
    if (!isProtectedRoute(path)) return;

    // Verify with Supabase storage before forcing redirect
    supabase.auth.getSession().then(({ data: { session: freshSession } }) => {
      if (freshSession) return; // session exists in storage — React state will catch up
      const returnPath = `${path}${search}`;
      sessionStorage.setItem('redirect_after_login', returnPath);
      window.location.replace('/login');
    });
  }, [authLoading, session]);
  
  const handleSignOut = async () => {
    try {
      clearPaymentFlowFlags();
      try { sessionStorage.removeItem('ggj_payment_page_active'); } catch { /* */ }
      await signOut();
    } catch (error) {
      console.error('Sign-out error:', error);
    }
  };
  
  const renderCurrentView = () => {
    const navigate = navigateHelper(onNavigate);

    // URL-first: honour the address bar even when currentView is stale (popstate races).
    if (typeof window !== 'undefined') {
      const path = window.location.pathname.replace(/\/+$/, '') || '/';
      const search = window.location.search;
      const searchParams = new URLSearchParams(search);
      const hasPaymentParams = searchParams.has('order_id') || searchParams.has('subscription_id') || searchParams.has('failed');

      if (path === '/payment-failed') {
        // Render if: session exists, auth still loading, payment flow active, OR URL has payment params.
        // Only redirect to '/' if we are CERTAIN the user should not be here.
        if (session || authLoading || isPaymentFlowInProgress() || hasPaymentParams) {
          return <PaymentFailedPage onNavigate={navigate} />;
        }
        // Also check sessionStorage for a recently-rendered payment page (prevents re-render bounces
        // after clearPaymentFlowFlags removes localStorage flags).
        try {
          if (sessionStorage.getItem('ggj_payment_page_active') === path) {
            return <PaymentFailedPage onNavigate={navigate} />;
          }
        } catch { /* best-effort */ }
        window.location.replace('/');
        return null;
      }

      if (
        isPaymentReturnPath(path, search)
        || (isPaymentFlowInProgress() && (path === '/payment-success' || path === '/'))
      ) {
        return <PaymentSuccessPage onNavigate={navigate} />;
      }

      if (path === '/payment-success') {
        // While auth is loading or URL has payment params, never redirect away.
        if (authLoading || hasPaymentParams) {
          return <PaymentSuccessPage onNavigate={navigate} />;
        }
        // Check the session-level flag set when the page first mounts
        try {
          if (sessionStorage.getItem('ggj_payment_page_active') === path) {
            return <PaymentSuccessPage onNavigate={navigate} />;
          }
        } catch { /* best-effort */ }
        if (!session && !isPaymentFlowInProgress()) {
          window.location.replace('/');
          return null;
        }
      }

      // Authenticated users must never see the marketing homepage (back-button bypass).
      // Immediately redirect to agent — never show spinner or homepage.
      if (session && !authLoading && (path === '/' || path === '/login' || path === '/signup')) {
        window.location.replace(POST_AUTH_LANDING_PATH);
        return null;
      }
    }

    // ─────────────────────────────────────────────────────────────────────
    // SECURITY: Defense-in-depth gate.
    //
    // Even though AppStateManager is supposed to redirect signed-out users
    // away from protected routes, a brief frame can slip through during
    // initial render races (e.g. on a hard refresh of /dashboard while
    // signed out, before useAuthentication resolves). This gate ensures a
    // protected view NEVER mounts without an authenticated session — if
    // we're still resolving auth we show a neutral spinner; if there's
    // definitively no session the parent's redirect-to-/login effect runs
    // and we render nothing in the meantime.
    //
    // We derive the protected-ness from the URL via the canonical route
    // registry so adding a new authenticated path stays a one-file change.
    // ─────────────────────────────────────────────────────────────────────
    if (typeof window !== 'undefined') {
      const path = window.location.pathname;
      const search = window.location.search;
      const isProtected = isProtectedRoute(path);
      const paymentReturn = isPaymentReturnPath(path, search);
      if (isProtected && !session && !paymentReturn) {
        return (
          <div
            className="min-h-screen flex items-center justify-center"
            role="status"
            aria-busy="true"
            aria-label="Verifying session"
          >
            <div className="w-8 h-8 rounded-full border-2 border-[#6C47FF]/20 border-t-[#6C47FF] animate-spin" />
          </div>
        );
      }
    }

    switch (currentView) {
      case 'home':
        return <HomePage onNavigate={navigate} />;
      
      case 'login':
        return (
          <Auth 
            initialView="login"
            onNavigate={navigate}
            onAuthSuccess={onAuthSuccess}
          />
        );
      
      case 'signup':
        return (
          <Auth 
            initialView="signup"
            onNavigate={navigate}
            onAuthSuccess={onAuthSuccess}
          />
        );

      case 'signup-consent':
        return (
          <SignupConsentPage
            onNavigate={navigate}
            onConsentComplete={onAuthSuccess}
          />
        );
      
      case 'optimize': {
        // ── DEPRECATED ROUTE ─────────────────────────────────────────────
        // The /optimize and /optimize/<step> URLs were the entry points for
        // the legacy SingleOptimizeFlow. That flow has been retired —
        // optimization is now performed by the AI Agent at /agent.
        //
        // We immediately redirect to /agent (signed-in) or /signup (guest)
        // and render a small spinner while the URL flips. We deliberately
        // use replaceState so the user can't navigate back to /optimize.
        // The original guest+signed-in render logic, plus the
        // SingleOptimizeFlow import, was removed when this route was
        // deprecated; the component now lives in
        // src/_deadcode/SingleOptimizeFlow/.
        if (authLoading) {
          return (
            <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50 dark:bg-gray-900" role="status" aria-label="Loading">
              <div className="w-8 h-8 rounded-full border-2 border-[#6C47FF]/20 border-t-[#6C47FF] animate-spin" />
            </div>
          );
        }
        const targetPath = session ? '/agent' : '/signup';
        window.history.replaceState({}, '', targetPath);
        // Use a microtask so the redirect happens after this render commits.
        // Without it the URL flips but the view stays on /optimize until the
        // next tick — visually identical, but harder to reason about.
        queueMicrotask(() => {
          window.dispatchEvent(new PopStateEvent('popstate'));
        });
        return (
          <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50 dark:bg-gray-900" role="status" aria-label="Redirecting">
            <div className="w-8 h-8 rounded-full border-2 border-[#6C47FF]/20 border-t-[#6C47FF] animate-spin" />
          </div>
        );
      }
      
      case 'onboarding':
        return (
          <OnboardingPage
            onComplete={() => {
              // Hard navigate to agent — replaceState + popstate is unreliable
              window.location.replace(POST_AUTH_LANDING_PATH);
            }}
          />
        );

      case 'agent':
        return (
          <PlanStatusProvider>
            <DashboardContainer onSignOut={handleSignOut} />
          </PlanStatusProvider>
        );

      case 'referrals':
        return (
          <PlanStatusProvider>
            <ReferralPage onSignOut={handleSignOut} />
          </PlanStatusProvider>
        );

      case 'admin-analytics':
        return (
          <PlanStatusProvider>
            <AdminAnalytics onSignOut={handleSignOut} />
          </PlanStatusProvider>
        );

      case 'control-panel':
        return (
          <PlanStatusProvider>
            <ControlPanel onSignOut={handleSignOut} />
          </PlanStatusProvider>
        );

      case 'cron-jobs-admin':
        return (
          <PlanStatusProvider>
            <CronJobsAdminPage onSignOut={handleSignOut} />
          </PlanStatusProvider>
        );

      case 'testimonials-admin':
        return (
          <PlanStatusProvider>
            <TestimonialsAdminPage onSignOut={handleSignOut} />
          </PlanStatusProvider>
        );

      case 'translations-admin':
        return (
          <PlanStatusProvider>
            <TranslationsAdminPage onSignOut={handleSignOut} />
          </PlanStatusProvider>
        );

      case 'dev-datatable':
        if (!import.meta.env.DEV || !DevDataTablePage) {
          return <NotFoundPage onNavigate={navigate} />;
        }
        return (
          <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-6 h-6 border-2 border-[#6C47FF]/20 border-t-[#6C47FF] rounded-full animate-spin" /></div>}>
            <DevDataTablePage />
          </React.Suspense>
        );

      case 'dev-coachmark':
        if (!import.meta.env.DEV || !DevCoachMarkPage) {
          return <NotFoundPage onNavigate={navigate} />;
        }
        return (
          <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-6 h-6 border-2 border-[#6C47FF]/20 border-t-[#6C47FF] rounded-full animate-spin" /></div>}>
            <DevCoachMarkPage />
          </React.Suspense>
        );

      case 'dashboard-selector':
        return <DashboardSelector onNavigate={navigate} />;
      
      case 'dashboard':
      case 'profile':
      case 'account-settings':
      case 'blog-cms':
      case 'blog-editor':
        return (
          <PlanStatusProvider>
            <DashboardContainer onSignOut={handleSignOut} />
          </PlanStatusProvider>
        );
      
      case 'forgot-password':
        return <ForgotPasswordPage onNavigate={navigate} />;
      
      case 'reset-password':
        return <ResetPasswordPage onNavigate={navigate} />;
      
      case 'auth-confirm':
        return (
          <AuthConfirmPage onNavigate={navigate} />
        );
      
      case 'privacy-policy':
        return <PrivacyPolicyPage onNavigate={navigate} />;
      
      case 'cookie-policy':
        return <CookiePolicyPage onNavigate={navigate} />;
      
      case 'terms-of-service':
        return <TermsOfServicePage onNavigate={navigate} />;
      
      case 'gdpr-compliance':
        return <GDPRCompliancePage onNavigate={navigate} />;

      case 'dpdp-compliance':
        return <DPDPCompliancePage onNavigate={navigate} />;
      
      case 'trust-center':
        return <TrustCenterPage onNavigate={navigate} />;
      
      case 'refund-cancellation':
        return <RefundCancellationPage onNavigate={navigate} />;
      
      case 'faq':
      case 'faqs':
        return <FAQPage onNavigate={navigate} />;
      
      case 'about-us':
        return <AboutUsPage onNavigate={navigate} />;
      
      case 'blog':
        return <BlogPage onNavigate={navigate} />;

      case 'blog-post':
        return <BlogPostPage onNavigate={navigate} />;

      case 'careers':
        return <CareersPage onNavigate={navigate} />;

      case 'careers-post': {
        // Slug is encoded as "careers-post:<slug>" from CareersPage navigation
        // or resolved from window.location.pathname directly in CareerPostPage
        const rawSlug = typeof window !== 'undefined'
          ? window.location.pathname.replace(/^\/careers\//, '').replace(/\/$/, '')
          : '';
        return <CareerPostPage slug={rawSlug} onNavigate={navigate} />;
      }

      case 'careers-apply': {
        // Extract the slug from /careers/<slug>/apply
        const applySlug = typeof window !== 'undefined'
          ? (window.location.pathname.match(/^\/careers\/([^/]+)\/apply/) ?? [])[1] ?? ''
          : '';
        return <JobApplicationPage slug={applySlug} onNavigate={navigate} />;
      }

      case 'success-story': {
        // Public page at /success/<slug>
        const successSlug = typeof window !== 'undefined'
          ? window.location.pathname.replace(/^\/success\//, '').replace(/\/$/, '')
          : '';
        return <SuccessStoryPage slug={successSlug} onNavigate={navigate} />;
      }
      
      case 'contact-us':
        return <ContactUsPage onNavigate={navigate} />;

      case 'email-unsubscribe':
        return <EmailUnsubscribePage />;
      
      case 'pricing':
        return <PricingPage onNavigate={navigate} />;
      
      case 'credits-checkout':
        return <CreditsCheckoutPage onNavigate={navigate} />;

      case 'payment-checkout':
        return <PaymentCheckoutPage />;
      
      case 'payment-success':
        return <PaymentSuccessPage onNavigate={navigate} />;

      case 'payment-failed':
        return <PaymentFailedPage onNavigate={navigate} />;
      
      case 'payment-history':
        // This case should never be reached - payment-history redirects to dashboard in AppStateManager
        return (
          <PlanStatusProvider>
            <DashboardContainer onSignOut={handleSignOut} />
          </PlanStatusProvider>
        );
      
      case 'debug-logs':
        return <DebugLogs />;
      
      case 'join-cohort':
        return <JoinCohortPage onNavigate={navigate} />;

      case 'developers':
        return <DevelopersPage onNavigate={navigate} />;
      
      case 'business':
        return <BusinessPage onNavigate={navigate} />;

      case 'job-board':
        return <PublicJobBoardPage onNavigate={navigate} />;

      case 'success-stories':
        return <SuccessStoriesPage onNavigate={navigate} />;

      case 'feature-smart-matching':
        return <FeatureSmartMatchingPage onNavigate={navigate} />;

      case 'feature-resume-optimizer':
        return <FeatureResumeOptimizerPage onNavigate={navigate} />;

      case 'feature-interview-prep':
        return <FeatureInterviewPrepPage onNavigate={navigate} />;

      case 'features-overview':
        return <FeaturesOverviewPage onNavigate={navigate} />;

      case 'problem':
        return <ProblemPage onNavigate={navigate} />;

      case 'who-its-for':
        return <WhoItsForPage onNavigate={navigate} />;

      case 'resume-check':
        return <ResumeCheckPage onNavigate={navigate} />;

      case 'not-found':
      default:
        return <NotFoundPage onNavigate={navigate} />;
    }
  };

  const isFullViewportShell = currentView === 'onboarding';

  if (isFullViewportShell) {
    return renderCurrentView();
  }

  return (
    <div className="min-h-screen flex flex-col">
      {renderCurrentView()}
    </div>
  );
};

export default AppRouter;
